import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";
import { ArrowLeft, Share2, PawPrint, Syringe, Phone, MapPin, Clipboard, Check, FileDown, Crown, FileText, Upload, Trash2, Download, Loader2, Sparkles, TrendingUp, Plus, BookOpen, Smile, Meh, Frown, QrCode, Briefcase } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { cn } from "@/lib/utils";
import ExtractRecordModal from "@/components/ExtractRecordModal";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

const MOOD_OPTIONS = [
  { value: "great", labelKey: "passport.moodGreat", icon: Smile, color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-200" },
  { value: "good", labelKey: "passport.moodGood", icon: Smile, color: "text-sky-600", bg: "bg-sky-50", ring: "ring-sky-200" },
  { value: "okay", labelKey: "passport.moodOkay", icon: Meh, color: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-200" },
  { value: "lethargic", labelKey: "passport.moodLethargic", icon: Frown, color: "text-orange-600", bg: "bg-orange-50", ring: "ring-orange-200" },
  { value: "concerned", labelKey: "passport.moodConcerned", icon: Frown, color: "text-rose-600", bg: "bg-rose-50", ring: "ring-rose-200" },
];

// Hoist chart-config objects to module scope so recharts isn't handed fresh
// references on every render — measurable perf win on long weight logs.
const CHART_MARGIN = { top: 12, right: 16, bottom: 4, left: -8 };
const CHART_AXIS_TICK = { fontSize: 11, fill: "#94A3B8" };
const CHART_TOOLTIP_CURSOR = { stroke: "#FF8A65", strokeDasharray: "3 3" };
const CHART_TOOLTIP_CONTENT_STYLE = { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 };
const CHART_TOOLTIP_LABEL_STYLE = { color: "#64748B" };
const CHART_LINE_DOT = { r: 3, strokeWidth: 2, stroke: "#FF8A65", fill: "#FFFFFF" };
const CHART_LINE_ACTIVE_DOT = { r: 5 };
const CHART_Y_DOMAIN = ["auto", "auto"];

function calculateAge(dob) {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  const years = today.getFullYear() - birth.getFullYear();
  return years === 0 ? `${today.getMonth() - birth.getMonth()} months` : `${years} year${years > 1 ? "s" : ""}`;
}

export default function PetPassport() {
  const { petId } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Time-ago helper uses translated "Just now" text — keep inside component so
  // it picks up the active language from i18n's React context.
  const timeAgo = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diff = Math.max(0, Date.now() - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return t("passport.justNow");
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [boardingCopied, setBoardingCopied] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Vet records state
  const [records, setRecords] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);
  const [extractFor, setExtractFor] = useState(null);
  const [justApplied, setJustApplied] = useState(0);

  // Weight log state
  const [weightLogs, setWeightLogs] = useState([]);
  const [newWeight, setNewWeight] = useState("");
  const [newWeightDate, setNewWeightDate] = useState("");
  const [weightSaving, setWeightSaving] = useState(false);
  const [weightError, setWeightError] = useState("");

  // Activity / care log state
  const [activityLogs, setActivityLogs] = useState([]);
  const [newActivityNote, setNewActivityNote] = useState("");
  const [newActivityMood, setNewActivityMood] = useState("");
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityError, setActivityError] = useState("");

  // AI scan quota (free: 1/month, premium: unlimited)
  const [aiUsage, setAiUsage] = useState(null);

  // Memoize the recharts dataset so the LineChart doesn't see a new array
  // reference (and re-render its entire SVG) on unrelated parent renders.
  const weightChartData = useMemo(
    () => weightLogs.map(l => ({
      date: l.date,
      label: l.date ? new Date(l.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "",
      weight: l.weight_kg,
    })),
    [weightLogs]
  );

  const fetchAiUsage = React.useCallback(() => {
    fetch(`${API}/ai/usage`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(setAiUsage);
  }, []);

  useEffect(() => {
    fetch(`${API}/pets/${petId}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setPet(d); setLoading(false); });
    fetch(`${API}/pets/${petId}/records`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(setRecords);
    fetch(`${API}/pets/${petId}/weight`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(setWeightLogs);
    fetch(`${API}/pets/${petId}/activity?limit=50`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(setActivityLogs);
    fetchAiUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- locals inside async closures are not deps
  }, [petId, fetchAiUsage]);

  const handleAddWeight = async (e) => {
    e.preventDefault();
    setWeightError("");
    const parsed = parseFloat(newWeight);
    if (!parsed || parsed <= 0 || parsed > 500) {
      setWeightError(t("passport.weightError"));
      return;
    }
    setWeightSaving(true);
    try {
      const res = await fetch(`${API}/pets/${petId}/weight`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          weight_kg: parsed,
          date: newWeightDate || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setWeightError(data.detail || t("passport.weightSaveError"));
        return;
      }
      setWeightLogs(prev => [...prev, data].sort((a, b) => (a.date || "").localeCompare(b.date || "")));
      setPet(prev => prev ? { ...prev, weight: data.weight_kg } : prev);
      setNewWeight("");
      setNewWeightDate("");
    } catch {
      setWeightError(t("common.networkError"));
    } finally {
      setWeightSaving(false);
    }
  };

  const handleDeleteWeight = async (logId) => {
    const res = await fetch(`${API}/pets/${petId}/weight/${logId}`, {
      method: "DELETE", credentials: "include",
    });
    if (res.ok) {
      setWeightLogs(prev => prev.filter(l => l.log_id !== logId));
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    setActivityError("");
    const note = newActivityNote.trim();
    if (!note) {
      setActivityError(t("passport.careLogAddError"));
      return;
    }
    if (note.length > 1000) {
      setActivityError(t("passport.careLogTooLong"));
      return;
    }
    setActivitySaving(true);
    try {
      const res = await fetch(`${API}/pets/${petId}/activity`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          note,
          mood: newActivityMood || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setActivityError(data.detail || t("passport.careLogSaveError"));
        return;
      }
      setActivityLogs(prev => [data, ...prev]);
      setNewActivityNote("");
      setNewActivityMood("");
    } catch {
      setActivityError(t("common.networkError"));
    } finally {
      setActivitySaving(false);
    }
  };

  const handleDeleteActivity = async (logId) => {
    const res = await fetch(`${API}/pets/${petId}/activity/${logId}`, {
      method: "DELETE", credentials: "include",
    });
    if (res.ok) {
      setActivityLogs(prev => prev.filter(l => l.log_id !== logId));
    }
  };

  const handlePickRecord = () => fileInputRef.current?.click();

  const handleRecordChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadError("");
    if (file.type !== "application/pdf") { setUploadError("Only PDF files are supported."); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadError("File too large. Max 10 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/pets/${petId}/records`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json();
      if (!res.ok) { setUploadError(data.detail || "Upload failed."); return; }
      setRecords(prev => [data, ...prev]);
      // Auto-open the AI extract modal on successful upload.
      setExtractFor(data);
    } catch {
      setUploadError("Network error. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadRecord = async (record) => {
    try {
      const res = await fetch(`${API}/pets/${petId}/records/${record.record_id}`, { credentials: "include" });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = record.filename || "record.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    // Drop the noisy console.error — toast/UX already informs the user
  } catch {
    // swallow — caller already shows the standard error toast/path
    }
  };

  const handleDeleteRecord = async (record) => {
    if (!window.confirm(t("passport.deleteRecordConfirm", { file: record.filename }))) return;
    const res = await fetch(`${API}/pets/${petId}/records/${record.record_id}`, {
      method: "DELETE", credentials: "include",
    });
    if (res.ok) setRecords(prev => prev.filter(r => r.record_id !== record.record_id));
  };

  const handleShare = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/pets/${petId}/generate-share`, { method: "POST", credentials: "include" });
      const data = await res.json();
      const url = `${window.location.origin}/passport/share/${data.share_token}`;
      setShareUrl(url);
      // Mirror the new token onto the pet object so the QR card stays visible
      // across reloads without a second `generate-share` round-trip.
      setPet(prev => prev ? { ...prev, share_token: data.share_token } : prev);
    } finally { setGenerating(false); }
  };

  // Hydrate the share URL from the pet doc on initial load — if the user has
  // already generated a link in a previous session, the QR + copy box appear
  // immediately instead of forcing them to click "Generate" again.
  useEffect(() => {
    if (pet?.share_token && !shareUrl) {
      setShareUrl(`${window.location.origin}/passport/share/${pet.share_token}`);
    }
  }, [pet?.share_token, shareUrl]);

  const qrRef = useRef(null);

  const handleDownloadQr = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    // Serialize the SVG → render onto a 2x scaled canvas → download as PNG.
    // 2x gives a crisp ~512px image suitable for print/poster use.
    const svgString = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const size = 512;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      canvas.toBlob((png) => {
        if (!png) return;
        const a = document.createElement("a");
        const safeName = (pet?.name || "pet").replace(/[^a-z0-9]/gi, "_");
        a.href = URL.createObjectURL(png);
        a.download = `PetAtlas_${safeName}_QR.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      }, "image/png");
    };
    img.src = url;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Boarding-view link is the same passport URL with ?view=boarding appended.
  // Same token, same auth — only the rendering switches on the public side.
  const copyBoardingLink = () => {
    if (!shareUrl) return;
    const boardingUrl = `${shareUrl}?view=boarding`;
    navigator.clipboard.writeText(boardingUrl).then(() => {
      setBoardingCopied(true);
      setTimeout(() => setBoardingCopied(false), 2000);
    });
  };

  const isPremium = user?.plan === "premium";

  const handleExportPdf = async () => {
    if (!isPremium) {
      navigate("/settings");
      return;
    }
    try {
      const res = await fetch(`${API}/pets/${petId}/passport.pdf`, { credentials: "include" });
      if (!res.ok) {
        alert(t("passport.exportFailed"));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PetAtlas_Passport_${(pet?.name || "pet").replace(/[^a-z0-9]/gi, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert(t("common.networkError"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: "#FAF9F6" }}>
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 py-12 space-y-4">
          <div className="h-64 rounded-3xl shimmer-bg" />
        </div>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="min-h-screen" style={{ background: "#FAF9F6" }}>
        <Navigation />
        <div className="text-center py-20">
          <p className="text-slate-500">{t("passport.petNotFound")}</p>
          <button onClick={() => navigate("/dashboard")} className="mt-4 px-6 py-2 rounded-full bg-[#FF8A65] text-white text-sm">{t("passport.backToDashboard")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-['Figtree']" style={{ background: "#FAF9F6" }}>
      <Navigation />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-4 mb-8 animate-fade-in-up">
          <button onClick={() => navigate("/dashboard")} className="p-2 rounded-xl hover:bg-white hover:shadow-sm text-slate-500 transition-all">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-800 font-['Nunito']">{t("passport.title")}</h1>
            <p className="text-slate-500 text-sm">{t("passport.subtitle")}</p>
          </div>
        </div>

        {/* Passport Card */}
        <div className="bg-white rounded-3xl shadow-[0_16px_48px_rgba(0,0,0,0.08)] border border-slate-100 overflow-hidden mb-6 animate-fade-in-up delay-100">
          {/* Passport header band */}
          <div className="h-3 bg-gradient-to-r from-[#FF8A65] via-[#FFD54F] to-[#81C784]" />
          <div className="p-8">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                {pet.photo_url ? (
                  <img src={pet.photo_url} alt={pet.name} className="w-28 h-28 rounded-2xl object-cover shadow-md" />
                ) : (
                  <div className="w-28 h-28 rounded-2xl bg-[#FFF5E6] flex items-center justify-center shadow-md">
                    <PawPrint size={44} className="text-[#FF8A65]" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{t("passport.passportLabel")}</div>
                <h2 className="text-3xl font-black text-slate-800 font-['Nunito']">{pet.name}</h2>
                <p className="text-slate-500 font-medium capitalize mt-1">{pet.species}{pet.breed ? ` · ${pet.breed}` : ""}</p>
                {pet.date_of_birth && (
                  <p className="text-sm text-slate-500 mt-1">
                    {t("passport.born")} {formatDate(pet.date_of_birth)} · {calculateAge(pet.date_of_birth)} {t("passport.oldSuffix")}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  {pet.color && <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">{pet.color}</span>}
                  {pet.weight && <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">{pet.weight} kg</span>}
                  {pet.microchip_id && <span className="text-xs bg-[#E3F2FD] text-[#1565C0] px-2.5 py-1 rounded-full font-medium">{t("passport.chipPrefix")} {pet.microchip_id}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Vet info */}
          {(pet.vet_name || pet.vet_phone) && (
            <div className="px-8 pb-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">{t("passport.veterinarian")}</h3>
              <div className="bg-[#F8F9FA] rounded-2xl p-4 space-y-2">
                {pet.vet_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                      <Syringe size={13} className="text-[#66BB6A]" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{pet.vet_name}</span>
                  </div>
                )}
                {pet.vet_phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#E3F2FD] flex items-center justify-center">
                      <Phone size={13} className="text-[#42A5F5]" />
                    </div>
                    <a href={`tel:${pet.vet_phone}`} className="text-sm font-medium text-[#1565C0] hover:underline">{pet.vet_phone}</a>
                  </div>
                )}
                {pet.vet_address && (
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#FFF5E6] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin size={13} className="text-[#FF8A65]" />
                    </div>
                    <span className="text-sm text-slate-600">{pet.vet_address}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Emergency contacts */}
          {pet.emergency_contacts?.length > 0 && (
            <div className="px-8 pb-6" data-testid="passport-emergency-contacts">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">{t("passport.emergencyContacts")}</h3>
              <div className="rounded-2xl overflow-hidden border border-slate-100 divide-y divide-slate-100">
                {pet.emergency_contacts.map((c, i) => (
                  <div key={`ec-${i}`} className="flex items-center justify-between px-4 py-3 bg-white">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{c.name || "—"}</span>
                        {c.relationship && <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">{c.relationship}</span>}
                      </div>
                      {c.phone && <a href={`tel:${c.phone}`} className="text-xs text-[#1565C0] hover:underline">{c.phone}</a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Medical notes */}
          {pet.medical_notes && (
            <div className="px-8 pb-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">{t("passport.medicalNotes")}</h3>
              <div className="bg-[#FFFDE7] rounded-2xl p-4 flex items-start gap-3 border border-[#FFF9C4]">
                <Clipboard size={15} className="text-[#F57F17] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-700">{pet.medical_notes}</p>
              </div>
            </div>
          )}

          {/* Vaccinations */}
          {pet.vaccinations?.length > 0 && (
            <div className="px-8 pb-8">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">{t("passport.vaccinationRecords")}</h3>
              <div className="rounded-2xl overflow-hidden border border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">{t("passport.vaccineColumn")}</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">{t("passport.givenColumn")}</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">{t("passport.nextDueColumn")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pet.vaccinations.map((v, i) => (
                      <tr key={`${v.name || "vax"}-${v.date || i}`} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                        <td className="px-4 py-3 font-medium text-slate-800">{v.name}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(v.date)}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-[#E8F5E9] text-[#388E3C] px-2 py-0.5 rounded-full font-medium">{formatDate(v.next_due)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Share section */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 animate-fade-in-up delay-200">
          <h3 className="text-base font-bold text-slate-800 font-['Nunito'] mb-2">{t("passport.shareTitle")}</h3>
          <p className="text-sm text-slate-500 mb-4">{t("passport.shareSubtitle")}</p>
          {!shareUrl ? (
            <button data-testid="generate-share-btn" onClick={handleShare} disabled={generating} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF8A65] text-white text-sm font-semibold hover:bg-[#FF7043] transition-colors disabled:opacity-60">
              <Share2 size={15} /> {generating ? t("passport.generating") : t("passport.generateShareLink")}
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input readOnly value={shareUrl} className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-600 bg-slate-50 focus:outline-none" />
                <button data-testid="copy-share-link-btn" onClick={copyLink} className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${copied ? "bg-[#E8F5E9] text-[#388E3C]" : "bg-[#FF8A65] text-white hover:bg-[#FF7043]"}`}>
                  {copied ? <><Check size={14} /> {t("common.copied")}</> : <><Clipboard size={14} /> {t("common.copy")}</>}
                </button>
              </div>
              {/* Boarding/sitter variant — same URL, ?view=boarding appended.
                  Strips weight logs, vet records, care timeline. Surfaces
                  active medications instead. One link, two presentations. */}
              <button
                data-testid="copy-boarding-link-btn"
                onClick={copyBoardingLink}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border-2 transition-colors ${
                  boardingCopied
                    ? "bg-[#E8F5E9] border-[#A5D6A7] text-[#388E3C]"
                    : "bg-white border-[#FFD7AE] text-[#E65100] hover:bg-[#FFF5E6]"
                }`}
              >
                {boardingCopied ? (
                  <><Check size={14} /> {t("passport.boardingCopied")}</>
                ) : (
                  <><Briefcase size={14} /> {t("passport.boardingShareBtn")}</>
                )}
              </button>
            </div>
          )}
        </div>

        {/* QR code card — generates a printable, scannable code for the
            public passport URL. Useful for collars, boarding paperwork,
            poster tags, and offline vet visits. */}
        <div
          className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 mt-6 animate-fade-in-up delay-225"
          data-testid="qr-card"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-[#F1F5F9] flex items-center justify-center flex-shrink-0">
              <QrCode size={18} className="text-slate-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-bold text-slate-800 font-['Nunito']">{t("passport.qrTitle")}</h3>
              <p className="text-xs text-slate-500">{t("passport.qrSubtitle")}</p>
            </div>
          </div>

          {pet?.share_token ? (
            <div className="flex flex-col items-center gap-4">
              <div
                ref={qrRef}
                className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm"
                data-testid="qr-svg-wrapper"
              >
                <QRCodeSVG
                  value={`${window.location.origin}/passport/share/${pet.share_token}`}
                  size={192}
                  bgColor="#FFFFFF"
                  fgColor="#1E293B"
                  level="M"
                  includeMargin={false}
                />
              </div>
              <p className="text-sm text-slate-500 font-medium" data-testid="qr-caption">
                {t("passport.qrScanLabel")}
              </p>
              <button
                data-testid="qr-download-btn"
                onClick={handleDownloadQr}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 transition-colors"
              >
                <Download size={14} /> {t("passport.qrDownload")}
              </button>
            </div>
          ) : (
            <div
              className="text-center py-8 bg-slate-50 rounded-2xl border border-dashed border-slate-200"
              data-testid="qr-empty-state"
            >
              <QrCode size={28} className="text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500 font-medium">{t("passport.qrNoToken")}</p>
              <p className="text-xs text-slate-400 mt-1">{t("passport.qrNoTokenHint")}</p>
            </div>
          )}
        </div>

        {/* Weight & Growth */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 mt-6 animate-fade-in-up delay-225" data-testid="weight-log-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-[#FFF3E0] flex items-center justify-center flex-shrink-0">
              <TrendingUp size={18} className="text-[#FF8A65]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-bold text-slate-800 font-['Nunito']">{t("passport.weightTitle")}</h3>
              <p className="text-xs text-slate-500">{t("passport.weightSub", { name: pet.name })}</p>
            </div>
          </div>

          {/* Add weight form */}
          <form onSubmit={handleAddWeight} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 mb-5">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1">{t("passport.weightKg")}</label>
              <input
                type="number"
                step="0.01"
                min="0.1"
                max="500"
                value={newWeight}
                onChange={e => setNewWeight(e.target.value)}
                placeholder="e.g. 12.4"
                required
                data-testid="weight-input"
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1">{t("passport.dateOptional")}</label>
              <input
                type="date"
                value={newWeightDate}
                onChange={e => setNewWeightDate(e.target.value)}
                data-testid="weight-date"
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={weightSaving}
                data-testid="save-weight-btn"
                className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-[#FF8A65] text-white text-sm font-semibold hover:bg-[#FF7043] transition-colors disabled:opacity-60"
              >
                {weightSaving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {t("common.log")}
              </button>
            </div>
          </form>
          {weightError && (
            <div data-testid="weight-error" className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4">
              {weightError}
            </div>
          )}

          {/* Chart + history */}
          {weightLogs.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">{t("passport.weightEmpty", { name: pet.name })}</p>
          ) : (
            <>
              <div className="h-48 -ml-2" data-testid="weight-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={weightLogs.map(l => ({
                      date: l.date,
                      label: new Date(l.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
                      weight: l.weight_kg,
                    }))}
                    margin={{ top: 12, right: 16, bottom: 4, left: -8 }}
                  >
                    <CartesianGrid vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#94A3B8" }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                      domain={["auto", "auto"]}
                      tickFormatter={(v) => `${v}kg`}
                    />
                    <Tooltip
                      cursor={{ stroke: "#FF8A65", strokeDasharray: "3 3" }}
                      contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }}
                      formatter={(v) => [`${v} kg`, "Weight"]}
                      labelStyle={{ color: "#64748B" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="weight"
                      stroke="#FF8A65"
                      strokeWidth={2.5}
                      dot={{ r: 3, strokeWidth: 2, stroke: "#FF8A65", fill: "#FFFFFF" }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* Recent entries (last 5) */}
              <div className="mt-4 space-y-1.5">
                {[...weightLogs].slice(-5).reverse().map((l) => (
                  <div key={l.log_id} data-testid={`weight-entry-${l.log_id}`} className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg hover:bg-slate-50">
                    <span className="text-slate-500 text-xs">{new Date(l.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                    <span className="font-semibold text-slate-800">{l.weight_kg} kg</span>
                    <button
                      onClick={() => handleDeleteWeight(l.log_id)}
                      data-testid={`delete-weight-${l.log_id}`}
                      className="text-slate-300 hover:text-red-400 transition-colors p-1"
                      title="Delete entry"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Activity / Care Log */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 mt-6 animate-fade-in-up delay-200" data-testid="activity-log-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-[#FFF5E6] flex items-center justify-center flex-shrink-0">
              <BookOpen size={18} className="text-[#FF8A65]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-slate-800 font-['Nunito']">{t("passport.careLogTitle")}</h3>
              <p className="text-xs text-slate-500">{t("passport.careLogSub")}</p>
            </div>
          </div>

          <form onSubmit={handleAddActivity} className="mb-4" data-testid="activity-form">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {MOOD_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = newActivityMood === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setNewActivityMood(active ? "" : opt.value)}
                    data-testid={`mood-${opt.value}`}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all",
                      active
                        ? `${opt.bg} ${opt.color} ring-2 ${opt.ring}`
                        : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                    )}
                  >
                    <Icon size={13} />
                    {t(opt.labelKey)}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={newActivityNote}
                onChange={(e) => setNewActivityNote(e.target.value)}
                placeholder={t("passport.careLogPlaceholder")}
                maxLength={1000}
                data-testid="activity-note-input"
                className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
              />
              <button
                type="submit"
                disabled={activitySaving || !newActivityNote.trim()}
                data-testid="add-activity-btn"
                className="flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-2xl bg-[#FF8A65] text-white text-sm font-semibold hover:bg-[#FF7043] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {activitySaving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {t("common.log")}
              </button>
            </div>
            {activityError && (
              <div data-testid="activity-error" className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-xl">
                {activityError}
              </div>
            )}
          </form>

          {activityLogs.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <BookOpen size={24} className="text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">{t("passport.careLogEmpty")}</p>
              <p className="text-xs text-slate-400 mt-1">{t("passport.careLogEmptyHint")}</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="activity-log-list">
              {activityLogs.slice(0, 10).map((l) => {
                const moodMeta = MOOD_OPTIONS.find((o) => o.value === l.mood);
                const MoodIcon = moodMeta?.icon;
                return (
                  <div
                    key={l.log_id}
                    data-testid={`activity-entry-${l.log_id}`}
                    className="group flex items-start gap-3 p-3 rounded-2xl border border-slate-100 hover:border-[#FF8A65]/40 hover:bg-[#FFF5E6]/40 transition-colors"
                  >
                    <div className={cn(
                      "w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0",
                      moodMeta ? moodMeta.bg : "bg-slate-50"
                    )}>
                      {MoodIcon ? <MoodIcon size={15} className={moodMeta.color} /> : <BookOpen size={14} className="text-slate-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 break-words">{l.note}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {timeAgo(l.created_at)}
                        {moodMeta ? <span className="ml-2 lowercase">· {t(moodMeta.labelKey).toLowerCase()}</span> : null}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteActivity(l.log_id)}
                      data-testid={`delete-activity-${l.log_id}`}
                      className="text-slate-300 hover:text-red-400 transition-colors p-1 opacity-0 group-hover:opacity-100"
                      title={t("common.delete")}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
              {activityLogs.length > 10 && (
                <div className="text-center text-xs text-slate-400 pt-2">
                  {t("passport.careLogShowing", { count: activityLogs.length })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Vet Records */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 mt-6 animate-fade-in-up delay-250" data-testid="vet-records-card">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-2xl bg-[#E3F2FD] flex items-center justify-center flex-shrink-0">
                <FileText size={18} className="text-[#1565C0]" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-800 font-['Nunito']">{t("passport.vetRecordsTitle")}</h3>
                <p className="text-xs text-slate-500">{t("passport.vetRecordsSub")}</p>
              </div>
            </div>
            <button
              data-testid="upload-record-btn"
              onClick={handlePickRecord}
              disabled={uploading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FF8A65] text-white text-xs font-semibold hover:bg-[#FF7043] transition-colors disabled:opacity-60 flex-shrink-0"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploading ? t("passport.uploading") : t("passport.uploadPdf")}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleRecordChange}
              className="hidden"
              data-testid="vet-record-file-input"
            />
          </div>

          {/* AI scan quota meter — explicit value framing converts free taste → premium habit */}
          {aiUsage && (
            <div
              data-testid="ai-usage-strip"
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 rounded-2xl mb-3 text-xs font-medium",
                aiUsage.unlimited
                  ? "bg-[#E8F5E9] text-[#2E7D32] border border-[#A5D6A7]"
                  : (aiUsage.remaining ?? 0) > 0
                    ? "bg-[#FFF5E6] text-[#E65100] border border-[#FFD7AE]"
                    : "bg-slate-50 text-slate-500 border border-slate-200"
              )}
            >
              <Sparkles size={13} className="flex-shrink-0" />
              {aiUsage.unlimited ? (
                <span data-testid="ai-usage-unlimited"><Trans i18nKey="passport.aiUnlimited" components={{ b: <b /> }} /></span>
              ) : (aiUsage.remaining ?? 0) > 0 ? (
                <span data-testid="ai-usage-remaining" className="flex-1">
                  <Trans i18nKey="passport.aiRemaining" count={aiUsage.remaining} values={{ count: aiUsage.remaining }} components={{ b: <b /> }} />{" "}
                  <Sparkles size={11} className="inline -mt-0.5" /> {t("passport.aiAutoDetect")}
                </span>
              ) : (
                <span data-testid="ai-usage-empty" className="flex-1">
                  {t("passport.aiUsedMsg")} <button onClick={() => navigate("/settings")} className="underline font-semibold hover:text-[#FF8A65]" data-testid="ai-usage-upgrade">{t("passport.aiUpgradeLink")}</button>
                </span>
              )}
            </div>
          )}

          {uploadError && (
            <div data-testid="record-error" className="text-sm text-red-700 bg-red-50 border border-red-100 px-4 py-2 rounded-xl mb-3">
              {uploadError}
            </div>
          )}

          {justApplied > 0 && (
            <div data-testid="extract-success-banner" className="flex items-center gap-2 text-sm text-[#2E7D32] bg-[#E8F5E9] border border-[#A5D6A7] px-4 py-2.5 rounded-xl mb-3">
              <Sparkles size={14} /> {t("passport.extractTitle", { count: justApplied })}
            </div>
          )}

          {records.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <FileText size={24} className="text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">{t("passport.recordsEmpty")}</p>
              <p className="text-xs text-slate-400 mt-1">{t("passport.recordsEmptyHint")}</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="vet-records-list">
              {records.map(r => (
                <div
                  key={r.record_id}
                  data-testid={`vet-record-${r.record_id}`}
                  className="flex items-center gap-3 p-3 rounded-2xl border border-slate-100 hover:border-[#FF8A65]/40 hover:bg-[#FFF5E6]/40 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#FFEBEE] flex items-center justify-center flex-shrink-0">
                    <FileText size={16} className="text-[#E53935]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-slate-800 truncate">{r.filename}</div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {formatDate(r.uploaded_at)} · {(r.size / 1024).toFixed(0)} KB
                    </div>
                  </div>
                  <button
                    data-testid={`extract-record-${r.record_id}`}
                    onClick={() => setExtractFor(r)}
                    title="Auto-detect reminders with AI"
                    className="p-2 rounded-xl hover:bg-white text-slate-500 hover:text-[#FF8A65] transition-colors"
                  >
                    <Sparkles size={15} />
                  </button>
                  <button
                    data-testid={`download-record-${r.record_id}`}
                    onClick={() => handleDownloadRecord(r)}
                    title="Download"
                    className="p-2 rounded-xl hover:bg-white text-slate-500 hover:text-[#1565C0] transition-colors"
                  >
                    <Download size={15} />
                  </button>
                  <button
                    data-testid={`delete-record-${r.record_id}`}
                    onClick={() => handleDeleteRecord(r)}
                    title="Delete"
                    className="p-2 rounded-xl hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Export PDF */}
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 mt-6 animate-fade-in-up delay-300">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#FFF9C4] flex items-center justify-center flex-shrink-0">
              <FileDown size={22} className="text-[#FF8A65]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-bold text-slate-800 font-['Nunito']">{t("passport.exportTitle")}</h3>
                {!isPremium && (
                  <span className="text-[10px] bg-[#FFF5E6] text-[#FF8A65] px-2 py-0.5 rounded-full font-bold flex items-center gap-0.5">
                    <Crown size={9} /> {t("nav.premium")}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mb-4">{t("passport.exportSub")}</p>
              <button
                data-testid="export-pdf-btn"
                onClick={handleExportPdf}
                className={cn(
                  "flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors",
                  isPremium
                    ? "bg-[#FF8A65] text-white hover:bg-[#FF7043]"
                    : "bg-slate-100 text-slate-700 hover:bg-[#FFF5E6] border border-slate-200"
                )}
              >
                <FileDown size={15} />
                {isPremium ? t("passport.exportBtn") : t("passport.upgradeToExport")}
              </button>
            </div>
          </div>
        </div>
      </main>

      <ExtractRecordModal
        open={!!extractFor}
        petId={petId}
        record={extractFor}
        onClose={() => setExtractFor(null)}
        onApplied={(n) => {
          setJustApplied(n);
          setTimeout(() => setJustApplied(0), 6000);
        }}
        onQuotaChange={fetchAiUsage}
      />
    </div>
  );
}
